import { useState } from 'react'
import { GlassmorphicCard } from '@/components/ui/glassmorphic-card'
import { Plus, Pencil, Key, X, UserPlus, Phone, Mail, User } from 'lucide-react'
import { mockCounsellors, type AdminCounsellor } from '@/data/admin-mock-data'
import { toast } from 'sonner'

export function AdminCounsellorsView() {
    const [counsellors, setCounsellors] = useState<AdminCounsellor[]>(mockCounsellors)
    const [modalOpen, setModalOpen] = useState(false)
    const [credentialsModalOpen, setCredentialsModalOpen] = useState(false)
    const [credentialsFor, setCredentialsFor] = useState<AdminCounsellor | null>(null)
    const [editingId, setEditingId] = useState<string | null>(null)
    const [form, setForm] = useState({
        name: '',
        email: '',
        phone: '',
        status: 'active' as 'active' | 'inactive',
        tempPassword: ''
    })

    const openCreate = () => {
        setEditingId(null)
        setForm({ name: '', email: '', phone: '', status: 'active', tempPassword: '' })
        setModalOpen(true)
    }

    const openEdit = (c: AdminCounsellor) => {
        setEditingId(c.id)
        setForm({
            name: c.name,
            email: c.email,
            phone: c.phone,
            status: c.status,
            tempPassword: ''
        })
        setModalOpen(true)
    }

    const openCreateCredentials = (c: AdminCounsellor) => {
        setCredentialsFor(c)
        const randomPass = Math.random().toString(36).slice(-8)
        setForm(f => ({ ...f, tempPassword: randomPass }))
        setCredentialsModalOpen(true)
    }

    const handleCreateCredentials = () => {
        if (!credentialsFor) return
        toast.success(`Credentials created for ${credentialsFor.name}. Login: ${credentialsFor.email}, Temp Password: ${form.tempPassword}. (Would send email in production)`)
        setCredentialsModalOpen(false)
        setCredentialsFor(null)
    }

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        if (editingId) {
            setCounsellors(prev => prev.map(x => x.id === editingId ? { ...x, ...form, activeLeads: x.activeLeads, totalLeads: x.totalLeads, createdAt: x.createdAt } : x))
            toast.success('Counsellor updated successfully')
        } else {
            const newCounsellor: AdminCounsellor = {
                id: `c-${Date.now()}`,
                ...form,
                activeLeads: 0,
                totalLeads: 0,
                createdAt: new Date().toISOString().split('T')[0]
            }
            setCounsellors(prev => [...prev, newCounsellor])
            toast.success('Counsellor added successfully')
        }
        setModalOpen(false)
    }

    return (
        <div className="space-y-6 pb-24 lg:pb-0">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-theme-primary">Manage Counsellors</h2>
                    <p className="text-sm text-theme-secondary">Add counsellors and generate their login credentials</p>
                </div>
                <button onClick={openCreate} className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary-blue hover:bg-blue-600 text-white font-semibold rounded-xl transition-colors">
                    <Plus className="h-5 w-5" />
                    Add Counsellor
                </button>
            </div>

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {counsellors.map((c) => (
                    <GlassmorphicCard key={c.id} className="p-5">
                        <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-white font-bold">
                                    {c.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                                </div>
                                <div>
                                    <p className="font-semibold text-theme-primary">{c.name}</p>
                                    <p className="text-xs text-theme-secondary">Joined: {new Date(c.createdAt).toLocaleDateString()}</p>
                                </div>
                            </div>
                            <div className="flex gap-1">
                                <button onClick={() => openEdit(c)} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-theme-secondary" title="Edit"><Pencil className="h-4 w-4" /></button>
                                <button onClick={() => openCreateCredentials(c)} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-theme-secondary" title="Create credentials"><Key className="h-4 w-4" /></button>
                            </div>
                        </div>

                        <div className="space-y-2 mb-4">
                            <div className="flex items-center gap-2 text-sm text-theme-secondary">
                                <Mail className="h-3.5 w-3.5" />
                                <span className="truncate">{c.email}</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-theme-secondary">
                                <Phone className="h-3.5 w-3.5" />
                                <span>{c.phone || 'N/A'}</span>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-2 mb-3">
                            <div className="bg-slate-50 dark:bg-slate-800/50 p-2 rounded-lg text-center">
                                <p className="text-xs text-theme-muted">Active Leads</p>
                                <p className="font-bold text-primary-blue">{c.activeLeads}</p>
                            </div>
                            <div className="bg-slate-50 dark:bg-slate-800/50 p-2 rounded-lg text-center">
                                <p className="text-xs text-theme-muted">Total Leads</p>
                                <p className="font-bold text-theme-primary">{c.totalLeads}</p>
                            </div>
                        </div>

                        <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${c.status === 'active' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' : 'bg-slate-200 dark:bg-slate-700 text-theme-secondary'}`}>
                            {c.status === 'active' ? 'Active' : 'Inactive'}
                        </span>
                    </GlassmorphicCard>
                ))}
            </div>

            {/* Edit/Create Modal */}
            {modalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setModalOpen(false)}>
                    <GlassmorphicCard className="w-full max-w-md p-6 shadow-xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-bold text-theme-primary">{editingId ? 'Edit Counsellor' : 'Add Counsellor'}</h3>
                            <button onClick={() => setModalOpen(false)} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"><X className="h-5 w-5" /></button>
                        </div>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-theme-secondary mb-1">Name</label>
                                <div className="relative">
                                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-theme-muted" />
                                    <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="w-full pl-9 pr-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary" placeholder="Counsellor name" required />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-theme-secondary mb-1">Email (Login)</label>
                                <div className="relative">
                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-theme-muted" />
                                    <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="w-full pl-9 pr-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary" placeholder="counsellor@neet.com" required />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-theme-secondary mb-1">Phone</label>
                                <div className="relative">
                                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-theme-muted" />
                                    <input type="tel" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="w-full pl-9 pr-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary" placeholder="9876543210" />
                                </div>
                            </div>

                            <label className="flex items-center gap-2">
                                <input type="checkbox" checked={form.status === 'active'} onChange={e => setForm(f => ({ ...f, status: e.target.checked ? 'active' : 'inactive' }))} className="rounded border-slate-300" />
                                <span className="text-sm text-theme-secondary">Active Status</span>
                            </label>

                            <div className="flex gap-2 pt-2">
                                <button type="button" onClick={() => setModalOpen(false)} className="flex-1 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-theme-primary">Cancel</button>
                                <button type="submit" className="flex-1 py-2 rounded-lg bg-primary-blue text-white font-semibold">Save</button>
                            </div>
                        </form>
                    </GlassmorphicCard>
                </div>
            )}

            {/* Credentials Modal */}
            {credentialsModalOpen && credentialsFor && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setCredentialsModalOpen(false)}>
                    <GlassmorphicCard className="w-full max-w-md p-6 shadow-xl" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-bold text-theme-primary flex items-center gap-2"><UserPlus className="h-5 w-5" />Create Credentials</h3>
                            <button onClick={() => setCredentialsModalOpen(false)} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"><X className="h-5 w-5" /></button>
                        </div>
                        <p className="text-sm text-theme-secondary mb-4">Generate login credentials for <strong>{credentialsFor.name}</strong></p>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-theme-secondary mb-1">Email (Login)</label>
                                <input value={credentialsFor.email} readOnly className="w-full px-4 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-theme-secondary mb-1">Temporary Password</label>
                                <input value={form.tempPassword} onChange={e => setForm(f => ({ ...f, tempPassword: e.target.value }))} className="w-full px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary font-mono" placeholder="Generate or enter" />
                                <button type="button" onClick={() => setForm(f => ({ ...f, tempPassword: Math.random().toString(36).slice(-8) }))} className="text-xs text-primary-blue hover:underline mt-1">Regenerate</button>
                            </div>
                            <p className="text-xs text-theme-muted">Credentials will be sent via email. Counsellor can change password on first login.</p>
                        </div>
                        <div className="flex gap-2 mt-6">
                            <button type="button" onClick={() => setCredentialsModalOpen(false)} className="flex-1 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-theme-primary">Cancel</button>
                            <button onClick={handleCreateCredentials} className="flex-1 py-2 rounded-lg bg-primary-blue text-white font-semibold flex items-center justify-center gap-2">
                                <Key className="h-4 w-4" /> Create & Send
                            </button>
                        </div>
                    </GlassmorphicCard>
                </div>
            )}
        </div>
    )
}
